/**
 * Scenarios — entity-configured list (§1E).
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Loader2,
  Pencil,
  Settings,
  Trash2,
  X,
  GitBranch,
} from 'lucide-react';
import type { FacetDefinition, SortOption, TableColumnWithRender } from '../../../components/collection/types';
import {
  activateScenario,
  BOUND_INPUT_TYPES,
  createScenario,
  deleteScenario,
  duplicateScenario,
  getScenarioPriorityCounts,
  getScenarios,
  linkScenarioPlanCycle,
  updateScenario,
} from '../../../lib/planningEngineService';
import { listPlanCycles } from '../../../lib/planningService';
import type { PlanCycle } from '../../../types/planning';
import { usePlanningEngine } from './PlanningEngineContext';
import { PlanningEntityListPage } from './PlanningEntityListPage';
import type { Scenario } from '../../../lib/planningEngineModule.types';
import type { EngineInputSource } from '../../../lib/planningEngineService';
import { PLANNING_MODE_LABELS } from '../../../lib/planningEngineModule.types';
import { usePlanningDefaultsStore } from '../../../stores/prioritization/planningDefaultsStore';

// ─── Scope resolution (mirrors resolveScenarioBoundEngineInput) ──────────────

interface ResolvedScope {
  label: string;
  tone: 'bound' | 'scoped' | 'warning';
}

/**
 * A scenario with no explicit Input Scope AND no priority rows of its own
 * silently resolves to "All Live Items" at run time (resolveScenarioBoundEngineInput
 * in planningEngineService.ts). Two scenarios that look identical on every other
 * setting can produce wildly different results this way — this label makes that
 * visible in the list instead of requiring a trip into the Input Scope picker.
 */
function resolveScopeLabel(scenario: Scenario, priorityCount: number | undefined): ResolvedScope {
  const boundType = scenario.inputSourceType as EngineInputSource['type'] | null | undefined;
  if (boundType && BOUND_INPUT_TYPES.has(boundType) && boundType !== 'scenario_portfolio' && scenario.inputSourceId) {
    return { label: scenario.inputSourceName?.trim() || 'Bound scope', tone: 'bound' };
  }
  const count = priorityCount ?? 0;
  if (count > 0) {
    return { label: `${count} priorit${count === 1 ? 'y' : 'ies'} (this scenario)`, tone: 'scoped' };
  }
  return { label: 'All Live Items', tone: 'warning' };
}

function ScopeBadge({ scope }: { scope: ResolvedScope }) {
  const classes =
    scope.tone === 'warning'
      ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)] dark:border-[var(--mustard)]'
      : scope.tone === 'bound'
        ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/40 dark:border-[var(--slate)]'
        : 'bg-[var(--surface)] text-[var(--text-sec)] border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:text-[var(--text-sec)] dark:border-[var(--rule)]';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${classes}`}
      title={
        scope.tone === 'warning'
          ? 'No priorities have been added to this scenario, so it runs against every live item in the workspace — not just the ones you might expect.'
          : undefined
      }
    >
      {scope.tone === 'warning' && <AlertTriangle className="h-3 w-3 flex-shrink-0" />}
      {scope.label}
    </span>
  );
}

// ─── Cycle binding ────────────────────────────────────────────────────────────

function usePlanCycles(): PlanCycle[] {
  const [cycles, setCycles] = useState<PlanCycle[]>([]);
  useEffect(() => {
    let cancelled = false;
    void listPlanCycles().then(({ data }) => {
      if (!cancelled) setCycles(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return cycles;
}

function cycleOptionLabel(c: PlanCycle): string {
  return c.status === 'active' ? c.name : `${c.name} — ${c.status}`;
}

/** Binding narrows options; absence never blocks — "no cycle" is always valid. */
function CycleBindingSelect({
  value,
  cycles,
  onChange,
  disabled,
}: {
  value: string | null | undefined;
  cycles: PlanCycle[];
  onChange: (planCycleId: string | null) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
      className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
    >
      <option value="">What-if (no cycle)</option>
      {cycles.map((c) => (
        <option key={c.id} value={c.id}>
          {cycleOptionLabel(c)}
        </option>
      ))}
    </select>
  );
}

// ─── Create modal ─────────────────────────────────────────────────────────────

export function CreateScenarioModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { projectId } = usePlanningEngine();
  const workspaceDefaults = usePlanningDefaultsStore((s) => s.defaults);
  const cycles = usePlanCycles();
  const [draft, setDraft] = useState<Partial<Scenario>>({
    name: '',
    planningMode: workspaceDefaults.planningMode,
    planningWeeks: workspaceDefaults.planningWeeks,
    capacityBufferPercent: workspaceDefaults.bufferPct / 100,
    defaultWorkUnitHours: workspaceDefaults.defaultWuHours,
    includeNotReady: false,
    includeBlocked: false,
    planCycleId: null,
    notes: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!draft.name?.trim() || !projectId) return;
    setSaving(true);
    setError(null);
    try {
      await createScenario(projectId, draft);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const field = (label: string, children: ReactNode, hint?: string) => (
    <div>
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-[var(--text-ter)] mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-[var(--text-ter)]" />
            <h3 className="text-base font-semibold text-[var(--text-pri)]">New scenario</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {field('Name', (
            <input
              autoFocus
              type="text"
              value={draft.name ?? ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); }}
              placeholder="e.g. Conservative Q3, Stretch goal"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          ))}

          {field('Planning mode', (
            <select
              value={draft.planningMode ?? 'priority_estimate'}
              onChange={(e) => setDraft({ ...draft, planningMode: e.target.value as Scenario['planningMode'] })}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="priority_estimate">Priority Estimate</option>
              <option value="work_units">Work Units</option>
              <option value="hybrid_remaining">Hybrid / Remaining</option>
            </select>
          ))}

          {field('Cycle binding', (
            <CycleBindingSelect
              value={draft.planCycleId}
              cycles={cycles}
              onChange={(planCycleId) => setDraft({ ...draft, planCycleId })}
            />
          ), 'Optional. Binding pre-fills the Propose step and bridges launch assumptions into the cycle.')}

          <div className="grid grid-cols-2 gap-4">
            {field('Planning weeks', (
              <input
                type="number"
                min={1}
                value={draft.planningWeeks ?? 12}
                onChange={(e) => setDraft({ ...draft, planningWeeks: Number(e.target.value) })}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            ))}
            {field('Capacity buffer', (
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={Math.round((draft.capacityBufferPercent ?? 0.2) * 100)}
                  onChange={(e) => setDraft({ ...draft, capacityBufferPercent: Number(e.target.value) / 100 })}
                  className="w-full border border-[var(--rule)] px-3 py-2 pr-7 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--text-ter)]">%</span>
              </div>
            ))}
            {field('How uncertainty is counted', (
              <select
                value={draft.uncertaintyBasis ?? 'buffer'}
                onChange={(e) => setDraft({ ...draft, uncertaintyBasis: e.target.value as 'buffer' | 'work_type' })}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="buffer">A buffer across the scenario — hold back the capacity above</option>
                <option value="work_type">Each work type's own multiplier — the buffer is ignored</option>
              </select>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {field('Default WU hours', (
              <input
                type="number"
                min={1}
                value={draft.defaultWorkUnitHours ?? 8}
                onChange={(e) => setDraft({ ...draft, defaultWorkUnitHours: Number(e.target.value) })}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            ), 'Hours per work unit when none is set')}
            <div className="flex flex-col gap-3 pt-6">
              <label className="flex items-center gap-2 text-sm text-[var(--text-sec)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.includeNotReady ?? false}
                  onChange={(e) => setDraft({ ...draft, includeNotReady: e.target.checked })}
                  className="rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-500"
                />
                Include not-ready items
              </label>
              <label className="flex items-center gap-2 text-sm text-[var(--text-sec)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.includeBlocked ?? false}
                  onChange={(e) => setDraft({ ...draft, includeBlocked: e.target.checked })}
                  className="rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-500"
                />
                Include blocked items
              </label>
            </div>
          </div>

          {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !draft.name?.trim()}
            className="px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create scenario
          </button>
        </div>
      </div>
    </div>
  );
}

const SORT_OPTIONS: SortOption<string>[] = [
  { field: 'name', label: 'Name' },
  { field: 'planningWeeks', label: 'Weeks' },
  { field: 'planningMode', label: 'Mode' },
];

const FACETS: FacetDefinition<string>[] = [
  {
    field: 'planningMode',
    label: 'Planning mode',
    type: 'multi',
    options: [
      { value: 'priority_estimate', label: 'Priority Estimate' },
      { value: 'work_units', label: 'Work Units' },
      { value: 'hybrid_remaining', label: 'Hybrid / Remaining' },
    ],
  },
];

function ScenarioDetailPanel({
  scenario,
  priorityCount,
  onUpdated,
  onDeleted,
  onActivate,
  onDuplicate,
  actionLoading,
}: {
  scenario: Scenario;
  priorityCount: number | undefined;
  onUpdated: () => void;
  onDeleted: () => void;
  onActivate: () => void;
  onDuplicate: () => void;
  actionLoading: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(scenario);
  const [saving, setSaving] = useState(false);
  const [binding, setBinding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cycles = usePlanCycles();

  useEffect(() => {
    setDraft(scenario);
    setEditing(false);
  }, [scenario]);

  const bindCycle = async (planCycleId: string | null) => {
    setBinding(true);
    setError(null);
    try {
      await linkScenarioPlanCycle(scenario.id, planCycleId);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBinding(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateScenario(scenario.id, draft);
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm('Delete this scenario? Capacity rows for it will also be removed.')) return;
    setSaving(true);
    try {
      await deleteScenario(scenario.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // In-place per-field editing (matches PriorityDetailPanel): every field
  // stays in the same grid position in both modes, and only the value itself
  // toggles between plain text and an input. Nothing appears or disappears
  // when Edit is clicked — the previous version swapped the whole body for a
  // shorter 4-field form, silently dropping Default WU hours, Include Not
  // Ready/Blocked, and reflowing the layout.
  const field = (label: string, display: ReactNode, input: ReactNode) => (
    <div>
      <dt className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">{label}</dt>
      <dd className="mt-1 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
        {editing ? input : display}
      </dd>
    </div>
  );

  const inputClass = 'w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] dark:bg-[var(--bg)]';

  return (
    <div className="p-6 space-y-5 h-full overflow-auto">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              className="w-full text-lg font-semibold border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          ) : (
            <h3 className="text-lg font-semibold truncate">{scenario.name}</h3>
          )}
          {scenario.active && (
            <span className="inline-flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Active scenario
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {editing ? (
            <>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving || !draft.name?.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(scenario);
                  setEditing(false);
                }}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => setEditing(true)} className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]" title="Edit">
                <Pencil className="h-4 w-4" />
              </button>
              {!scenario.active && (
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={onActivate}
                  className="p-2 text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]"
                  title="Activate"
                >
                  <CheckCircle2 className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                disabled={actionLoading}
                onClick={onDuplicate}
                className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                title="Duplicate"
              >
                <Copy className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => void remove()} disabled={saving} className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)]" title="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="text-sm text-[var(--coral)]">{error}</div>}

      <dl className="grid grid-cols-2 gap-4">
        {field(
          'Planning mode',
          PLANNING_MODE_LABELS[scenario.planningMode],
          <select
            className={inputClass}
            value={draft.planningMode}
            onChange={(e) => setDraft({ ...draft, planningMode: e.target.value as Scenario['planningMode'] })}
          >
            <option value="priority_estimate">Priority Estimate</option>
            <option value="work_units">Work Units</option>
            <option value="hybrid_remaining">Hybrid / Remaining</option>
          </select>,
        )}
        {field(
          'Planning weeks',
          scenario.planningWeeks,
          <input
            type="number"
            min={1}
            className={inputClass}
            value={draft.planningWeeks}
            onChange={(e) => setDraft({ ...draft, planningWeeks: Number(e.target.value) })}
          />,
        )}
        {field(
          'Capacity buffer',
          `${Math.round(scenario.capacityBufferPercent * 100)}%`,
          <div className="relative">
            <input
              type="number"
              min={0}
              max={100}
              className={inputClass}
              value={Math.round((draft.capacityBufferPercent ?? 0.2) * 100)}
              onChange={(e) => setDraft({ ...draft, capacityBufferPercent: Number(e.target.value) / 100 })}
            />
          </div>,
        )}
        {field(
          'How uncertainty is counted',
          (scenario.uncertaintyBasis ?? 'buffer') === 'work_type'
            ? "Each work type's own multiplier"
            : 'A buffer across the scenario',
          <select
            className={inputClass}
            value={draft.uncertaintyBasis ?? 'buffer'}
            onChange={(e) => setDraft({ ...draft, uncertaintyBasis: e.target.value as 'buffer' | 'work_type' })}
          >
            <option value="buffer">A buffer across the scenario — hold back the capacity above</option>
            <option value="work_type">Each work type&rsquo;s own multiplier — the buffer is ignored</option>
          </select>,
        )}
        {field(
          'Default WU hours',
          `${scenario.defaultWorkUnitHours}h`,
          <input
            type="number"
            min={1}
            className={inputClass}
            value={draft.defaultWorkUnitHours}
            onChange={(e) => setDraft({ ...draft, defaultWorkUnitHours: Number(e.target.value) })}
          />,
        )}
        {field(
          'Include not ready',
          scenario.includeNotReady ? 'Yes' : 'No',
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.includeNotReady}
              onChange={(e) => setDraft({ ...draft, includeNotReady: e.target.checked })}
              className="rounded border-[var(--rule)]"
            />
            Include not ready
          </label>,
        )}
        {field(
          'Include blocked',
          scenario.includeBlocked ? 'Yes' : 'No',
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.includeBlocked}
              onChange={(e) => setDraft({ ...draft, includeBlocked: e.target.checked })}
              className="rounded border-[var(--rule)]"
            />
            Include blocked
          </label>,
        )}
      </dl>

      <div>
        <dt className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Input scope</dt>
        <dd className="mt-1">
          <ScopeBadge scope={resolveScopeLabel(scenario, priorityCount)} />
          <p className="text-xs text-[var(--text-ter)] mt-1">
            The population this scenario actually plans against. Set the Input Scope picker
            on the Priorities tab to bind an Item Pool or similar named set — otherwise a
            scenario with no priorities of its own quietly runs against every live item.
          </p>
        </dd>
      </div>

      <div>
        <dt className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Cycle binding</dt>
        <dd className="mt-1">
          <CycleBindingSelect
            value={scenario.planCycleId}
            cycles={cycles}
            disabled={binding}
            onChange={(planCycleId) => void bindCycle(planCycleId)}
          />
          <p className="text-xs text-[var(--text-ter)] mt-1">
            Binding pre-fills the Propose step and bridges launch assumptions into the cycle.
            Unbound scenarios stay exploratory what-ifs.
          </p>
        </dd>
      </div>
    </div>
  );
}

export default function ScenarioList() {
  const { projectId, refreshScenarios, activeScenario } = usePlanningEngine();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [priorityCounts, setPriorityCounts] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getScenarios(projectId);
      setScenarios(list);
      const counts = await getScenarioPriorityCounts(list.map((s) => s.id));
      setPriorityCounts(counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { void load(); }, [load]);

  const tableColumns = useMemo((): TableColumnWithRender<Scenario>[] => [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      minWidth: 200,
      flex: true,
      render: (s) => (
        <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]" title={s.name}>{s.name}</span>
      ),
    },
    {
      key: 'planningMode',
      label: 'Mode',
      sortable: true,
      render: (s) => PLANNING_MODE_LABELS[s.planningMode],
    },
    {
      key: 'planningWeeks',
      label: 'Weeks',
      sortable: true,
      render: (s) => s.planningWeeks,
    },
    {
      key: 'capacityBufferPercent',
      label: 'Buffer',
      render: (s) => `${Math.round(s.capacityBufferPercent * 100)}%`,
    },
    {
      key: 'active',
      label: 'Active',
      render: (s) =>
        s.active ? <CheckCircle2 className="h-4 w-4 text-[var(--green)]" /> : <span className="text-[var(--text-ter)]">—</span>,
    },
    {
      key: 'scope',
      label: 'Scope',
      render: (s) => <ScopeBadge scope={resolveScopeLabel(s, priorityCounts[s.id])} />,
    },
  ], [priorityCounts]);

  const activeCount = scenarios.filter((s) => s.active).length;

  return (
    <>
    <PlanningEntityListPage<Scenario, string>
      items={scenarios}
      loading={loading}
      getId={(s) => s.id}
      title="Scenarios"
      subtitle={`${scenarios.length} scenario${scenarios.length !== 1 ? 's' : ''} · ${activeCount} active`}
      icon={<Settings className="h-4 w-4 text-[var(--text-ter)]" />}
      iconBgClassName="bg-[var(--slate-dim)]"
      itemLabel="scenario"
      searchFields={['name', 'notes']}
      sortOptions={SORT_OPTIONS}
      defaultSort={{ field: 'name', direction: 'asc' }}
      facets={FACETS}
      tableColumns={tableColumns}
      renderDetail={(s) => (
        <ScenarioDetailPanel
          scenario={s}
          priorityCount={priorityCounts[s.id]}
          actionLoading={actionLoading === s.id}
          onUpdated={() => {
            void load();
            void refreshScenarios();
          }}
          onDeleted={() => {
            setSelectedId(null);
            void load();
            void refreshScenarios();
          }}
          onActivate={() => {
            setActionLoading(s.id);
            void activateScenario(s.id, projectId!)
              .then(() => load())
              .then(() => refreshScenarios())
              .finally(() => setActionLoading(null));
          }}
          onDuplicate={() => {
            setActionLoading(s.id);
            void duplicateScenario(s.id)
              .then(() => load())
              .then(() => refreshScenarios())
              .finally(() => setActionLoading(null));
          }}
        />
      )}
      selectedId={selectedId}
      onSelectionChange={setSelectedId}
      urlParamName="scenario"
      onRefresh={() => void load()}
      onCreate={() => setShowCreateModal(true)}
      createLabel="New Scenario"
      emptyTitle="No scenarios yet"
      emptyDescription="Create a scenario to define planning assumptions — weeks, buffer, and capacity mode."
      emptyAction={{ label: 'New Scenario', onClick: () => setShowCreateModal(true) }}
      error={error}
      activeScenario={activeScenario}
    />

    {showCreateModal && (
      <CreateScenarioModal
        onClose={() => setShowCreateModal(false)}
        onCreated={() => {
          void load();
          void refreshScenarios();
        }}
      />
    )}
    </>
  );
}
